/*
 * Copyright © 2020-2024 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: caoran
 * @LastEditTime: 2025-11-07 15:05:32
 */
import React, { useEffect, useMemo, useState } from 'react'

import {
  Button,
  Table,
  DatePicker,
  Select,
  Form,
  Layout,
  message,
  Modal
} from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import {
  useAppDispatch,
  useAppSelector,
  usePayment,
  useVip
} from '../../../app/hooks'
import { fetchGroupOperateLog } from '../../../actions/team'
import CONFIG from '../../../constants/config'
import brandImg from '../../../images/department-empty.png'
import ContactUS from '../../components/ContactUS'
import sensors from '../../../utils/sensors'

import PageHeader from '@/components/PageHeader'
import './index.scss'

const { Content } = Layout
const FormItem = Form.Item
const { RangePicker } = DatePicker
const { Column } = Table

const pageSize = 10

interface LogRecord {
  operateTime: string
  nickname: string
  businessType: string
  resourceName: string
  content: string
  contentDiff: string
  operateType: string
  extra?: string
  index?: number
}

const BUSINESSTYPES = {
  //   WATERMARK: '水印变更',
  //   WATERMARK_REMOVE: '去水印权益',
  //   GROUP_MEMBER: '成员变更',
  WATERMARK: '团队水印',
  WATERMARK_REMOVE: '水印次数',
  GROUP_MEMBER: '团队成员',
  VISIT_SCORE: '拜访打分',
  DEPARTMENT: '部门',
  VIP: '会员信息',
  PERMISSION: '团队设置',
  GROUP_PHOTO_RECOVER: '团队照片恢复',
  GROUP_PHOTO: '团队照片',
  GROUP_ATTENDANCE: '团队考勤'
  // SyncOriginPic: '同步原图权限变更',
  // CloseOfficialWatermark: '无水印权限变更',
  // DEPARTMENT: '部门变更',
  // VIP: '会员信息'
}

const SensorsBusinessTypes = {
  WATERMARK: 'groupWatermarkEdit', // 团队水印
  WATERMARK_REMOVE: 'waterMarkCount', // 水印次数
  GROUP_MEMBER: 'memberEdit', // 团队成员
  SCORE_UPDATE: 'visitScore', // 拜访打分
  DEPARTMENT: 'department', // 部门变更
  VIP: 'vip', // 会员信息
  PERMISSION: 'permission', // 团队设置
  GROUP_PHOTO_RECOVER: 'groupPhotoRecover', // 团队照片恢复
  GROUP_PHOTO: 'groupPhoto', // 团队照片
  GROUP_ATTENDANCE: 'groupAttendance' // 团队考勤
  // 全部: 'all'
}

const OPERATETYPES = {
  CREATE: '添加',
  UPDATE: '更新',
  DELETE: '删除'
}

const VipRequired = React.memo(() => {
  const payment = usePayment()
  return (
    <Content className="vip-required">
      <img src={brandImg} alt="" />
      <p>
        一键查看90天内水印、会员权益、团队成员等核心信息的新增、修改、删除全轨迹，每一次操作都有迹可循，每一处变更都能溯源，彻底杜绝团队管理中“信息异动无据可查”的隐患！
        {'\n\n'}
        开通高级会员，即刻掌握团队管理的“透明仪表盘”，做能防患未然、高效合规的管理领袖！
      </p>

      <button
        onClick={() => payment.openSvip('webOperateLog', false, false, true)}
      >
        开通高级会员
      </button>
    </Content>
  )
})

const TeamOperationLog = () => {
  const dispatch = useAppDispatch()
  const groupID = useAppSelector((state) => state.group.groupId)
  const myRole = useAppSelector((state) => state.group.myRole)
  const [dates, setDates] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().add(-31, 'days').startOf('day'),
    dayjs().endOf('day')
  ])
  const isStartTimeAfterEndTime = useMemo(
    () => dates[0].isSameOrAfter(dates[1]),
    [dates]
  )
  const [data, setData] = useState<LogRecord[]>([])
  const [total, setTotal] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const { benefits } = useVip()
  const [businessType, setBusinessType] = useState('')
  const [imagePreviewVisible, setImagePreviewVisible] = useState(false)
  const [previewPhoto, setPreviewPhoto] = useState('')

  const [params, setParams] = useState({
    groupID,
    operateTimeStart: dayjs()
      .add(-31, 'days')
      .startOf('day')
      .format(CONFIG.timeFormat),
    operateTimeEnd: dayjs().endOf('day').format(CONFIG.timeFormat),
    pageSize: pageSize,
    pageNo: 1,
    businessType: ''
    // operateType: ''
  })

  const dataSource = useMemo(() => {
    return data.map((item, index) => {
      return {
        ...item,
        index
      }
    })
  }, [data])

  const handleClick = (photo: string) => {
    setImagePreviewVisible(true)
    setPreviewPhoto(photo)
  }
  const handleCancel = () => setImagePreviewVisible(false)

  const handleFetchLog = () => {
    dispatch(fetchGroupOperateLog({ ...params }))
      .then((data) => {
        if (data.status === 0) {
          setData(data.data)
          setTotal(data.total)
          if (data.total === 0) {
            message.info('该时间段没有操作过数据')
          }
        }
      })
      .catch((err) => {
        message.error('服务繁忙，请稍后重试')
      })
  }

  const handleSubmit = () => {
    handleOperateLogTrack('beginSearch')
    setCurrentPage(1)
    setParams({
      ...params,
      operateTimeStart: dates[0].format(CONFIG.timeFormat),
      operateTimeEnd: dates[1].format(CONFIG.timeFormat),
      businessType,
      pageNo: 1
    })
  }
  const onReset = () => {
    handleOperateLogTrack('reset')
    setDates([dayjs().add(-31, 'days').startOf('day'), dayjs().endOf('day')])
    setBusinessType('')
    setCurrentPage(1)
    setParams({
      groupID,
      operateTimeStart: dayjs()
        .add(-31, 'days')
        .startOf('day')
        .format(CONFIG.timeFormat),
      operateTimeEnd: dayjs().endOf('day').format(CONFIG.timeFormat),
      businessType: '',
      pageSize: pageSize,
      pageNo: 1
    })
  }

  const onPageChange = (page: number) => {
    setCurrentPage(page)
    setParams({ ...params, pageNo: page })
  }

  useEffect(() => {
    handleFetchLog()
  }, [params])

  // 上报埋点信息
  const handleSensorsTrack = (clickItem: string) => {
    sensors.track('web_click_operation_type', {
      clickItem,
      groupID,
      role: CONFIG.saKeywords.roles[myRole]
    })
  }

  const handleOperateLogTrack = (clickItem: string) => {
    sensors.track('web_click_operation_log', {
      clickItem,
      groupID,
      role: CONFIG.saKeywords.roles[myRole]
    })
  }

  return (
    <>
      <Layout className="OperateLog team-operation-log">
        <PageHeader
          className="Header"
          title="操作记录"
          subTitle="团队管理的 “铁证追溯系统”，90 天全流程管控让管理风险清零"
          extra={
            <>
              <span>想看更多团队操作记录信息？</span>
              <ContactUS
                style={{ display: 'inline-block', margin: 0 }}
                fromPage="团队操作记录"
              >
                <Button
                  type="link"
                  size="small"
                  style={{ margin: 0 }}
                  onClick={() => {
                    handleOperateLogTrack('contactUs')
                  }}
                >
                  联系客服
                </Button>
              </ContactUS>
            </>
          }
        />
        {benefits.operationRecord ? (
          <Content className="content">
            <div className="Filter filter-container">
              <Form layout="inline">
                <FormItem
                  label="筛选时间"
                  validateStatus={isStartTimeAfterEndTime ? 'error' : ''}
                  help={isStartTimeAfterEndTime ? '结束时间应晚于开始时间' : ''}
                >
                  <RangePicker
                    value={dates}
                    allowClear={false}
                    showTime={{ format: 'HH:mm' }}
                    disabledDate={(current) =>
                      current?.isAfter(dayjs(), 'days') ||
                      dayjs().diff(current, 'days') > 90
                    }
                    onChange={(value) => {
                      handleOperateLogTrack('dateChoose')
                      if (value && value[0] && value[1]) {
                        setDates([value[0], value[1]])
                      }
                    }}
                    style={{ width: '400px' }}
                    ranges={{
                      今天: [dayjs().startOf('day'), dayjs().endOf('day')],
                      最近一周: [dayjs().add(-7, 'days'), dayjs()],
                      最近一个月: [dayjs().add(-1, 'months'), dayjs()]
                    }}
                  />
                </FormItem>
                <FormItem label="业务类型">
                  <Select
                    style={{ width: '200px' }}
                    value={businessType}
                    onChange={(value) => {
                      setBusinessType(value)
                      handleOperateLogTrack('typeFiltering')
                      handleSensorsTrack(
                        SensorsBusinessTypes[value]
                          ? SensorsBusinessTypes[value]
                          : 'all'
                      )
                    }}
                  >
                    <Select.Option value="">全部</Select.Option>
                    {Object.keys(BUSINESSTYPES).map((key) => (
                      <Select.Option key={key} value={key}>
                        {BUSINESSTYPES[key]}
                      </Select.Option>
                    ))}
                  </Select>
                </FormItem>
                <FormItem label="" colon={false} style={{ marginLeft: '12px' }}>
                  <Button
                    disabled={isStartTimeAfterEndTime}
                    type="primary"
                    style={{ marginRight: '10px' }}
                    onClick={handleSubmit}
                  >
                    开始筛选
                  </Button>
                  <Button onClick={onReset}>重置</Button>
                </FormItem>
              </Form>
            </div>
            <Table
              rowKey="index"
              dataSource={dataSource}
              scroll={{ y: window.innerHeight - 360 }}
              pagination={{
                total,
                pageSize: pageSize,
                current: currentPage,
                showSizeChanger: false,
                onChange: onPageChange
              }}
            >
              <Column
                title="操作时间"
                width={200}
                dataIndex="operateTime"
                key="operateTime"
              />
              <Column title="操作人" dataIndex="nickname" key="nickname" />
              <Column
                title="业务类型"
                dataIndex="businessType"
                key="businessType"
                render={(text) => BUSINESSTYPES[text] || text}
              />
              <Column
                title="操作资源"
                dataIndex="resourceName"
                key="resourceName"
              />
              <Column
                title="操作内容"
                dataIndex="content"
                key="content"
                width="320px"
                render={(content, item) => {
                  console.log('content', content, item)
                  if (
                    item.operateType === 'DELETE' &&
                    item.businessType === 'GROUP_PHOTO'
                  ) {
                    return (
                      <img
                        style={{ width: '100px', cursor: 'pointer' }}
                        src={content + '?x-oss-process=image/resize,w_48&quot'}
                        alt=""
                        onClick={() => handleClick(content)}
                      />
                    )
                  }
                  return content
                }}
              />
              <Column
                title="变更内容"
                dataIndex="contentDiff"
                key="contentDiff"
              />
              <Column
                title="操作类型"
                dataIndex="operateType"
                key="operateType"
                render={(text) => OPERATETYPES[text] || text}
              />
              {/*<Column title="其他信息" dataIndex="extra" key="extra" />*/}
            </Table>
          </Content>
        ) : (
          <VipRequired />
        )}
      </Layout>
      <Modal
        open={imagePreviewVisible}
        footer={null}
        onCancel={handleCancel}
        closable
        closeIcon={
          <CloseOutlined style={{ color: 'white', fontSize: '24px' }} />
        }
        centered
        styles={{
          body: {
            padding: 0,
            textAlign: 'center',
            background: 'black',
            fontSize: '0'
          }
        }}
        width="100%"
      >
        <img
          onClick={handleCancel}
          src={previewPhoto}
          style={{
            maxWidth: '100%',
            height: '100vh',
            objectFit: 'contain'
          }}
        />
      </Modal>
    </>
  )
}

export default TeamOperationLog
